import { install } from "@github/hotkey";
import * as Sentry from "@sentry/browser";
import autosize from "autosize";
import { Current } from "current.js";

import DisabledButtonResetter from "./disabled_button_resetter";
import DrawerMenu from "./drawer_menu";
import JoinFormSupport from "./join_form_support";
import LikeButton from "./like_button";
import PostBodyExpander from "./post_body_expander";
import PostFormSpoilerEnabler from "./post_form_spoiler_enabler";
import PostFormStorage from "./post_form_storage";
import PostSpoilerCover from "./post_spoiler_cover";
import {
  AlgoliaDataSource,
  PostgresqlDataSource,
  SearchSuggestion,
} from "./search_suggestion";
import SeasonSelector from "./season_selector";

const jQuery = window.jQuery; // eslint-disable-line prefer-destructuring

/**
 * Display console log for developer.
 */
function consoleLogForDeveloper() {
  if (!window.console || typeof window.console.log !== "function") {
    return;
  }

  if (Current.env === "test") {
    return;
  }
}

/**
 * Initialize autosize for comment textarea if exists.
 */
function initCommentForm() {
  // Enable autosize only for PC access
  if (Current.devicetype !== "pc") {
    return;
  }

  const commentForm = document.getElementById("js-comment-form");
  if (commentForm) {
    autosize(commentForm);
  }
}

/**
 * Initialize drawer menu.
 */
function initDrawerMenu() {
  const drawerMenu = jQuery(".drawer-menu");
  if (drawerMenu) {
    const drawer = new DrawerMenu(drawerMenu, {
      menu: "#is-drawer-menu-invisible ul",
    });
    drawer.setup();
  }
}

/*
 * Install all the hotkeys on the page
 */
function initHotkey() {
  for (const el of document.querySelectorAll("[data-hotkey]")) {
    install(el);
  }
}

/**
 * Initialize posting support features.
 */
function initPostSupport() {
  // Store/Restore post form contents.
  // This procedure should run before other posting features.
  const $postForm = jQuery("#js-post-form");
  if (
    window.store.enabled &&
    $postForm.length > 0 &&
    $postForm.data("autoSave")
  ) {
    const postFormStorage = new PostFormStorage({
      subjectType: "Episode",
      subjectId: $postForm.data("episodeId"),
      formSelector: "#js-post-form",
      bodySelector: "#log_body",
      ratingSelector: "#js-post-form__rating",
      channelSelector: "#js-post-form__channel",
      spoilerSelector: "#log_spoiler",
    });
    postFormStorage.init();
  }

  // Spoiler enabler for post form
  const $postForms = jQuery(".js-post-form");
  if ($postForms.length > 0) {
    $postForms.each((_idx, element) => {
      const postFormSpoilerEnabler = new PostFormSpoilerEnabler({
        el: element,
        textareaSelector: ".js-post-form__body",
        checkboxSelector: ".js-post-form__spoiler",
      });
      postFormSpoilerEnabler.init();
    });
  }

  // Spoiler body blur effect
  const postSpoilerCover = new PostSpoilerCover();
  postSpoilerCover.init();
}

function initSentry() {
  if (Current.sentrydsn.length === 0) {
    return;
  }

  Sentry.init({
    dsn: Current.sentrydsn,
    // debug: true,
    release: Current.releaseVersion,
    environment: Current.environment,
    ignoreErrors: [],
    integrations: [],
    initialScope: {
      user: { id: Current.user.id, username: Current.user.username },
    },
  });
}

function initLikeButton() {
  const likeButton = new LikeButton();
  likeButton.init();
}

function initDisabledButtonResetter() {
  const disabledButtonResetter = new DisabledButtonResetter(
    "button[data-disable-with]:disabled",
  );
  disabledButtonResetter.init();
}

// ホームでの投稿本文の展開処理
function initPostBodyExpander() {
  if (jQuery(".js-post-more-link").length > 0) {
    const postBodyExpander = new PostBodyExpander({
      linkSelector: ".js-post-more-link",
      fulltextSelector: ".js-post-body-fulltext",
    });
    postBodyExpander.init();
  }
}

function initSearchSuggestion() {
  const inputElement = document.getElementById("js-input-search-keyword");
  if (inputElement == null) {
    return;
  }

  let dataSource = null;
  if (Current.algolia.applicationId) {
    dataSource = new AlgoliaDataSource({
      appId: Current.algolia.applicationId,
      apiKey: Current.algolia.apiKey,
      indexName: Current.algolia.indexName,
      limit: 10,
    });
  } else {
    dataSource = new PostgresqlDataSource();
  }

  const searchSuggestion = new SearchSuggestion({
    inputElement,
    dataSource,
    templateIds: {
      empty: "js-template-search-suggestion-empty",
      footer: "js-template-search-suggestion-footer",
      suggestion: "js-template-search-suggestion",
    },
  });
  searchSuggestion.init();
}

function initAccountSettings() {
  if (document.querySelector("body.page-settings") == null) {
    return;
  }

  // Click to select Personal Access Token field
  const accessTokenElement = document.getElementById(
    "js-personal_access_token",
  );
  if (accessTokenElement) {
    jQuery(accessTokenElement).on("click", (event) => {
      event.currentTarget.select();
    });
  }

  // Revoke Personal Access Token dialog
  const openDialogButtonId = "js-token-revoke-button";
  const openDialogButton = document.getElementById(openDialogButtonId);
  if (openDialogButton) {
    const dialogId = openDialogButton.getAttribute("data-dialog-id");
    const dialog = document.getElementById(dialogId);
    window.dialogPolyfill.registerDialog(dialog);
    const cancelButton = document.querySelector(
      `#${dialogId} .js-button--cancel`,
    );
    const closeDialog = () => {
      jQuery("body").removeClass("modal-is-open");
      jQuery(dialog).removeClass("is-show");
      dialog.close();
    };
    const openDialog = () => {
      dialog.showModal();
      jQuery(dialog).addClass("is-show");
      jQuery("body").addClass("modal-is-open");
    };
    jQuery(openDialogButton).on("click", openDialog);
    jQuery(cancelButton).on("click", closeDialog);
  }
}

function initSeasonSelector() {
  if (jQuery("#js-select-year").length === 0) {
    return;
  }
  const selector = new SeasonSelector({
    yearSelectId: "js-select-year",
    seasonSelectId: "js-select-season",
  });
  selector.init();
}

function initBootstrap() {
  jQuery('[data-toggle="tooltip"]').tooltip();
}

// Initialize jQuery timeago plugin via rails-timeago
function initTimeago() {
  if (Current.locale) {
    jQuery.timeago.settings.lang = Current.locale;
  }
}

function initJoinFormSupport() {
  if (jQuery("#js-join-form-input-username").length > 0) {
    const joinFormSupport = new JoinFormSupport();
    joinFormSupport.init();
  }
}

function initCharts() {
  // Bar chart
  if (jQuery(".js-bar-chart").length > 0) {
    jQuery(".js-bar-chart-count").each((_, element) => {
      const $element = jQuery(element);
      const value = $element.data("chart-count");
      $element.css("width", `${value * 100}%`);
      if (value === "0.0") {
        $element.addClass("is-zero-value");
      }
    });
  }

  // Column chart
  if (jQuery(".js-column-chart").length > 0) {
    let totalChartCount = 0;

    jQuery(".js-column-chart-count").each((_, element) => {
      const $element = jQuery(element);
      const count = $element.data("chart-count");
      totalChartCount += count;
      if (count === 0) {
        // 0%は文字色を変更する
        $element.addClass("is-zero-value");
        $element.prev().addClass("is-zero-value");
      }
    });

    jQuery(".js-column-chart-rate").each((_, element) => {
      const $element = jQuery(element);
      const rate = $element.data("chart-rate");
      $element.css("width", `${rate}%`);
      // 9%以下は文字表示なし
      if (rate < 10) {
        $element.addClass("is-low-value");
      }
      // 0%は非表示
      if (rate === 0) {
        $element.addClass("is-zero-value");
      }
    });

    if (totalChartCount > 0) {
      jQuery(".js-column-chart").removeClass("is-hidden");
    } else {
      jQuery(".js-column-chart").remove();
    }
  }
}

/**
 * Application initializer.
 */
export default function initApplication() {
  initSentry();
  initDrawerMenu();
  initHotkey();
  initPostSupport();
  initLikeButton();
  initDisabledButtonResetter();
  initPostBodyExpander();
  initSearchSuggestion();
  initSeasonSelector();
  initBootstrap();
  initTimeago();
  initJoinFormSupport();
  initCharts();
  initAccountSettings();
  initCommentForm();
  consoleLogForDeveloper();
}
